const { ensureAdSlotMinHeight } = require('./adSizeUtils');
const { injectStyles } = require('./utils/styleInjector');

const ARTICLE_SCOPE_SELECTORS = ['.rebellt-item', 'article', '.article-body', '.body-description'];
const POSTS_CONTAINER_SELECTOR = '.four-in-a-row .posts-custom';
const DEFAULT_MIN_DISTANCE_PX = 2200;
const DEFAULT_ITEM_INTERVAL = 4;
const ARTICLE_INFINITE_AD_STYLES_ID = 'milla-article-infinite-ad-styles';
const ARTICLE_INFINITE_AD_STYLES = `
.milla-article-infinite-ad {
  padding-bottom: 32px;
}
`;

function isElement(node) {
  return Boolean(node && node.nodeType === 1);
}

function ensureArticleInfiniteAdState() {
  if (typeof window === 'undefined') return null;
  if (!window.adData || typeof window.adData !== 'object') {
    window.adData = {};
  }
  if (typeof window.adData.placedAdCount !== 'number') {
    window.adData.placedAdCount = 1;
  }
  if (!window.adData.articleInfiniteAds || typeof window.adData.articleInfiniteAds !== 'object') {
    window.adData.articleInfiniteAds = {
      candidatesSeen: 0,
      inserted: 0
    };
  }
  return window.adData.articleInfiniteAds;
}

function getDocumentTop(node) {
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  const rect = node.getBoundingClientRect();
  const scrollY =
    typeof window !== 'undefined'
      ? (window.pageYOffset || document.documentElement.scrollTop || 0)
      : 0;
  return rect.top + scrollY;
}

function getArticleScope(node) {
  if (!node || typeof node.closest !== 'function') return node || null;
  for (let i = 0; i < ARTICLE_SCOPE_SELECTORS.length; i += 1) {
    const match = node.closest(ARTICLE_SCOPE_SELECTORS[i]);
    if (match) return match;
  }
  return node;
}

function getPostsContainer(node) {
  if (!isElement(node)) return null;
  if (node.matches && node.matches(POSTS_CONTAINER_SELECTOR)) return node;
  if (node.closest) {
    const closestPostsContainer = node.closest('.posts-custom');
    if (
      closestPostsContainer &&
      closestPostsContainer.closest &&
      closestPostsContainer.closest('.four-in-a-row')
    ) {
      return closestPostsContainer;
    }
  }
  if (node.matches && node.matches('.four-in-a-row')) {
    return node.querySelector ? node.querySelector('.posts-custom') : null;
  }
  if (node.querySelector) {
    return node.querySelector(POSTS_CONTAINER_SELECTOR);
  }
  return null;
}

function getEffectiveMinDistance(configuredDistance) {
  const configured = Number(configuredDistance);
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }
  if (typeof window === 'undefined') {
    return DEFAULT_MIN_DISTANCE_PX;
  }
  const viewportHeight = Number(window.innerHeight || 0);
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
    return DEFAULT_MIN_DISTANCE_PX;
  }
  return Math.max(DEFAULT_MIN_DISTANCE_PX, Math.round(viewportHeight * 2));
}

function findPreviousAdTop(anchor, slotPrefix) {
  if (typeof document === 'undefined') return null;
  const anchorTop = getDocumentTop(anchor);
  if (!Number.isFinite(anchorTop)) return null;

  const slots = Array.from(
    document.querySelectorAll(`.content-ad[id^="${slotPrefix}-"]`)
  );
  let previousTop = null;
  slots.forEach((slot) => {
    const top = getDocumentTop(slot);
    if (!Number.isFinite(top) || top >= anchorTop) return;
    if (previousTop === null || top > previousTop) {
      previousTop = top;
    }
  });
  return previousTop;
}

function isTooCloseToPreviousAd(anchor, slotPrefix, minDistancePx) {
  const anchorTop = getDocumentTop(anchor);
  const previousTop = findPreviousAdTop(anchor, slotPrefix);
  if (!Number.isFinite(anchorTop) || !Number.isFinite(previousTop)) {
    return false;
  }
  return anchorTop - previousTop < minDistancePx;
}

function createArticleInfiniteAdHtml(slotId, proLinkUrl, proLinkText) {
  injectStyles(ARTICLE_INFINITE_AD_STYLES_ID, ARTICLE_INFINITE_AD_STYLES);
  return ''
    + '<div class="ad-wrapper milla-article-infinite-ad" data-milla-article-infinite-ad="true">'
    + '<div class="ad-flex">'
    + `<a href="${proLinkUrl}" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">${proLinkText}</a>`
    + `<div id="${slotId}" class="content-ad"></div>`
    + '</div></div>';
}

function isAdLikeElement(node) {
  if (!isElement(node) || !node.classList) return true;
  if (node.classList.contains('ad-wrapper')) return true;
  if (node.classList.contains('milla-article-infinite-ad')) return true;
  if (node.classList.contains('separator')) return true;
  if (node.classList.contains('related-post')) return true;
  if (node.classList.contains('stories-player-container')) return true;
  if (node.matches && node.matches('script, style, template')) return true;
  if (node.querySelector && node.querySelector('.content-ad')) return true;
  return false;
}

function getPostsContainerItems(container) {
  if (!container || !container.children) return [];

  const wrappedItems = Array.from(container.children).reduce((items, child) => {
    if (!child || !child.classList || !child.classList.contains('posts-wrapper')) {
      return items;
    }
    return items.concat(
      Array.from(child.children).filter((item) => (
        item &&
        item.classList &&
        item.classList.contains('widget') &&
        !isAdLikeElement(item)
      ))
    );
  }, []);

  if (wrappedItems.length > 0) {
    return wrappedItems;
  }

  return Array.from(container.children).filter((child) => !isAdLikeElement(child));
}

function getArticleInfinitePostsContainers(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  return Array.from(root.querySelectorAll(POSTS_CONTAINER_SELECTOR)).filter(Boolean);
}

function hasNearbyInsertedAd(item) {
  const next = item && item.nextElementSibling;
  return Boolean(
    next &&
    next.classList &&
    (
      next.classList.contains('ad-wrapper') ||
      next.classList.contains('milla-article-infinite-ad')
    )
  );
}

function insertAdBeforeNode(node, options, state) {
  const { slotPrefix, proLinkUrl, proLinkText, minHeight, prepareSlot } = options;
  window.adData.placedAdCount += 1;
  const adNumber = window.adData.placedAdCount;
  const slotId = `${slotPrefix}-${adNumber}`;
  node.insertAdjacentHTML(
    'beforebegin',
    createArticleInfiniteAdHtml(slotId, proLinkUrl, proLinkText)
  );

  const inserted = node.previousElementSibling;
  if (inserted && inserted.dataset) {
    inserted.dataset.millaArticleInfiniteAdIndex = String(state ? state.inserted + 1 : 1);
  }

  const slot = document.getElementById(slotId);
  ensureAdSlotMinHeight(slot, minHeight);
  if (typeof prepareSlot === 'function') {
    prepareSlot(slot);
  }

  if (state) {
    state.inserted += 1;
    state.lastSlotId = slotId;
    state.lastInsertedAt = Date.now();
  }
  return true;
}

function insertAdAfterItem(item, options, state) {
  const { slotPrefix, proLinkUrl, proLinkText, minHeight, prepareSlot } = options;
  window.adData.placedAdCount += 1;
  const adNumber = window.adData.placedAdCount;
  const slotId = `${slotPrefix}-${adNumber}`;
  item.insertAdjacentHTML(
    'afterend',
    createArticleInfiniteAdHtml(slotId, proLinkUrl, proLinkText)
  );

  const inserted = item.nextElementSibling;
  if (inserted && inserted.dataset) {
    inserted.dataset.millaArticleInfiniteAdIndex = String(state ? state.inserted + 1 : 1);
  }

  const slot = document.getElementById(slotId);
  ensureAdSlotMinHeight(slot, minHeight);
  if (typeof prepareSlot === 'function') {
    prepareSlot(slot);
  }

  if (state) {
    state.inserted += 1;
    state.lastSlotId = slotId;
    state.lastInsertedAt = Date.now();
  }
  return true;
}

function scanArticleInfinitePostsContainer(container, options = {}) {
  if (typeof document === 'undefined' || !container) return 0;
  return scanArticleInfinitePostsContainers([container], options);
}

function scanArticleInfinitePostsContainers(containers, options = {}) {
  if (typeof document === 'undefined' || !containers) return 0;
  const state = ensureArticleInfiniteAdState();
  const interval =
    typeof options.itemInterval === 'number' && options.itemInterval > 0
      ? options.itemInterval
      : DEFAULT_ITEM_INTERVAL;
  const distance = getEffectiveMinDistance(options.minDistancePx);
  const uniqueContainers = Array.from(new Set(Array.from(containers).filter(Boolean)));
  const items = uniqueContainers.reduce((allItems, container) => (
    allItems.concat(getPostsContainerItems(container))
  ), []);
  let insertedCount = 0;

  items.forEach((item, index) => {
    if ((index + 1) % interval !== 0) return;
    if (!item || !item.dataset) return;
    if (item.dataset.millaArticleInfiniteAdAfter === 'true') return;
    if (hasNearbyInsertedAd(item)) {
      item.dataset.millaArticleInfiniteAdAfter = 'true';
      return;
    }
    if (isTooCloseToPreviousAd(item, options.slotPrefix, distance)) {
      return;
    }

    item.dataset.millaArticleInfiniteAdAfter = 'true';
    if (insertAdAfterItem(item, options, state)) {
      insertedCount += 1;
    }
  });

  return insertedCount;
}

function createArticleInfiniteAdInserter(options = {}) {
  const { slotPrefix, minDistancePx } = options;

  return function insertArticleInfiniteAd(node) {
    if (
      typeof document === 'undefined' ||
      !node ||
      typeof node.insertAdjacentHTML !== 'function' ||
      !slotPrefix
    ) {
      return false;
    }

    const postsContainer = getPostsContainer(node);
    if (postsContainer) {
      return scanArticleInfinitePostsContainers(getArticleInfinitePostsContainers(document), options) > 0;
    }

    const state = ensureArticleInfiniteAdState();
    const scope = getArticleScope(node);
    if (scope && scope.dataset && scope.dataset.millaArticleInfiniteAdScanned === 'true') {
      return false;
    }
    if (scope && scope.dataset) {
      scope.dataset.millaArticleInfiniteAdScanned = 'true';
    }
    if (state) {
      state.candidatesSeen += 1;
    }

    const distance = getEffectiveMinDistance(minDistancePx);
    if (isTooCloseToPreviousAd(node, slotPrefix, distance)) {
      return false;
    }

    return insertAdBeforeNode(node, options, state);
  };
}

function setupArticleInfiniteScrollAdsObserver(options = {}) {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return null;
  }
  if (typeof window !== 'undefined' && options.slotPrefix) {
    const guardKey = `__millaArticleInfiniteAdsObserver_${options.slotPrefix}`;
    if (window[guardKey]) {
      return window[guardKey];
    }
  }

  const scan = () => {
    scanArticleInfinitePostsContainers(getArticleInfinitePostsContainers(document), options);
  };

  const root = document.documentElement || document.body;
  if (!root) return null;

  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      scan();
    }, 100);
  };

  const observer = new MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i += 1) {
      const mutation = mutations[i];
      if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;
      for (let j = 0; j < mutation.addedNodes.length; j += 1) {
        const node = mutation.addedNodes[j];
        if (!isElement(node)) continue;
        if (
          (node.matches && (node.matches(POSTS_CONTAINER_SELECTOR) || node.matches('.posts-custom'))) ||
          (node.matches && (node.matches('.posts-wrapper') || node.matches('.widget'))) ||
          (node.querySelector && node.querySelector(POSTS_CONTAINER_SELECTOR)) ||
          (node.querySelector && (node.querySelector('.posts-wrapper') || node.querySelector('.widget'))) ||
          (node.parentElement && node.parentElement.matches && node.parentElement.matches(POSTS_CONTAINER_SELECTOR))
        ) {
          queueScan();
          return;
        }
      }
    }
  });

  observer.observe(root, { childList: true, subtree: true });
  setTimeout(scan, 100);
  setTimeout(scan, 1000);
  if (typeof window !== 'undefined' && options.slotPrefix) {
    const guardKey = `__millaArticleInfiniteAdsObserver_${options.slotPrefix}`;
    window[guardKey] = observer;
  }
  return observer;
}

module.exports = {
  createArticleInfiniteAdInserter,
  scanArticleInfinitePostsContainer,
  setupArticleInfiniteScrollAdsObserver
};
